import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { FaTachometerAlt, FaChevronDown, FaBars, FaTimes } from 'react-icons/fa';
import { ADMIN_MENU_GROUPS, findAdminMenuItem } from '../constants/adminMenu';
import AdminOverview from '../components/admin/AdminOverview';
import HeroCarouselManager from '../components/admin/HeroCarouselManager';
import SponsorManager from '../components/admin/SponsorManager';
import SectionManager from '../components/admin/SectionManager';
import SocialMediaManager from '../components/admin/SocialMediaManager';
import ContactManager from '../components/admin/ContactManager';
import LogoManager from '../components/admin/LogoManager';
import PopupManager from '../components/admin/PopupManager';
import CoinManager from '../components/admin/CoinManager';
import IPManager from '../components/admin/IPManager';
import UserManager from '../components/admin/UserManager';
import HomePageManager from '../components/admin/HomePageManager';
import MarketManager from '../components/admin/MarketManager';
import MarketPurchaseHistory from '../components/admin/MarketPurchaseHistory';
import BlackjackSettings from '../components/admin/BlackjackSettings';
import GameSettingsPanel from '../components/admin/GameSettingsPanel';
import KickManager from '../components/admin/KickManager';
import TicketEventManager from '../components/admin/TicketEventManager';
import TicketManager from '../components/admin/TicketManager';
import ReferralTicketManager from '../components/admin/ReferralTicketManager';
import BannerManager from '../components/admin/BannerManager';
import SponsorBannerManager from '../components/admin/SponsorBannerManager';
import PromoCodeManager from '../components/admin/PromoCodeManager';
import SpinEventManager from '../components/admin/SpinEventManager';
import TicketRaffleManager from '../components/admin/TicketRaffleManager';

const AdminDashboard = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => {
    const current = findAdminMenuItem(location.pathname);
    return current ? { [current.group.key]: true } : {};
  });

  // Keep the group containing the current page expanded when navigating directly to a link.
  useEffect(() => {
    const current = findAdminMenuItem(location.pathname);
    if (current) {
      setExpandedGroups((prev) => ({ ...prev, [current.group.key]: true }));
    }
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleGroup = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    window.dispatchEvent(new Event('adminTokenChanged'));
    window.location.href = '/';
  };

  const isOverview = location.pathname === '/admin' || location.pathname === '/admin/';
  const current = findAdminMenuItem(location.pathname);
  const currentTitle = isOverview ? 'Genel Bakış' : current?.item.label || 'Admin Panel';

  const sidebarContent = (
    <nav className="bg-gradient-card rounded-xl p-3 border border-gold/30">
      <Link
        to="/admin"
        onClick={() => setMobileMenuOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-lg font-semibold mb-2 transition-colors ${
          isOverview ? 'bg-gradient-to-r from-gold to-yellow-500 text-navy-900 shadow-gold' : 'text-white/85 hover:bg-navy-700 hover:text-gold'
        }`}
      >
        <FaTachometerAlt />
        Genel Bakış
      </Link>

      <div className="h-px bg-gold/15 my-2" />

      <ul className="space-y-1">
        {ADMIN_MENU_GROUPS.map((group) => {
          const GroupIcon = group.icon;
          const isExpanded = !!expandedGroups[group.key];
          const groupHasActive = group.items.some((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'));

          return (
            <li key={group.key}>
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 rounded-lg text-left text-xs font-bold uppercase tracking-wider transition-colors ${
                  groupHasActive ? 'text-gold' : 'text-white/45 hover:text-white/75'
                }`}
              >
                <GroupIcon className="text-sm shrink-0" />
                <span className="flex-1 truncate">{group.label}</span>
                <FaChevronDown className={`text-[10px] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {isExpanded && (
                <ul className="mt-0.5 mb-1.5 ml-3 pl-3 border-l border-gold/15 space-y-0.5">
                  {group.items.map((item) => {
                    const ItemIcon = item.icon;
                    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            isActive
                              ? 'bg-gradient-to-r from-gold to-yellow-500 text-navy-900 shadow-gold'
                              : 'text-white/70 hover:bg-navy-700 hover:text-gold'
                          }`}
                        >
                          <ItemIcon className="text-xs shrink-0 opacity-80" />
                          <span className="truncate">{item.label}</span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );

  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Admin Header */}
      <div className="bg-navy-800 border-b border-gold/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                type="button"
                onClick={() => setMobileMenuOpen((v) => !v)}
                className="lg:hidden text-gold p-1.5 -ml-1.5 shrink-0"
                aria-label="Menüyü aç/kapat"
              >
                {mobileMenuOpen ? <FaTimes /> : <FaBars />}
              </button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-2xl font-bold text-gold truncate">Admin Panel</h1>
                <p className="hidden sm:block text-xs text-white/40 truncate">{currentTitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 shrink-0">
              <Link
                to="/"
                className="bg-gradient-to-r from-gold to-yellow-500 hover:from-yellow-500 hover:to-gold text-navy-900 font-bold px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 shadow-gold text-sm"
              >
                Ana Sayfa
              </Link>
              <button
                onClick={handleLogout}
                className="bg-red-500 hover:bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm"
              >
                Çıkış
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
          {/* Sidebar - desktop: always visible; mobile: toggled */}
          <div className={`lg:col-span-1 ${mobileMenuOpen ? 'block' : 'hidden'} lg:block`}>
            <div className="lg:sticky lg:top-24">{sidebarContent}</div>
          </div>

          {/* Main Content */}
          <div className={`lg:col-span-3 ${mobileMenuOpen ? 'hidden lg:block' : 'block'}`}>
            <Routes>
              <Route index element={<AdminOverview />} />
              <Route path="hero-carousel" element={<HeroCarouselManager />} />
              <Route path="homepage" element={<HomePageManager />} />
              <Route path="logo" element={<LogoManager />} />
              <Route path="popups" element={<PopupManager />} />
              <Route path="sponsors" element={<SponsorManager />} />
              <Route path="sections" element={<SectionManager />} />
              <Route path="social-media" element={<SocialMediaManager />} />
              <Route path="contacts" element={<ContactManager />} />
              <Route path="market" element={<MarketManager />} />
              <Route path="market-purchases" element={<MarketPurchaseHistory />} />
              <Route path="blackjack" element={<BlackjackSettings />} />
              <Route path="mines" element={<GameSettingsPanel gameType="mines" title="Mines Ayarları" defaults={{ minBet: 10, maxBet: 250, rtp: 0.97, minMines: 1, maxMines: 24 }} showMines />} />
              <Route path="limbo" element={<GameSettingsPanel gameType="limbo" title="Limbo Ayarları" defaults={{ minBet: 1, maxBet: 100, rtp: 0.99, maxWin: 20000 }} showMaxWin />} />
              <Route path="kick" element={<KickManager />} />
              <Route path="users" element={<UserManager />} />
              <Route path="coins" element={<CoinManager />} />
              <Route path="ips" element={<IPManager />} />
              <Route path="ticket-events" element={<TicketEventManager />} />
              <Route path="tickets" element={<TicketManager />} />
              <Route path="referral-tickets" element={<ReferralTicketManager />} />
              <Route path="ticket-raffle" element={<TicketRaffleManager />} />
              <Route path="banners" element={<BannerManager />} />
              <Route path="sponsor-banners" element={<SponsorBannerManager />} />
              <Route path="promo-codes" element={<PromoCodeManager />} />
              <Route path="spin-events" element={<SpinEventManager />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
